import { useCallback, useState } from 'react';
import { supabaseEventService } from '../supabase/event.service';
import { supabaseEventSeriesService } from '../supabase/eventSeries.service';
import stripeService from '../supabase/stripe.service';

export interface PublishEventResult {
  success: boolean;
  limitReached?: boolean;
  eventsUsed?: number;
  eventsLimit?: number;
  error?: string;
}

interface UseEventActionsReturn {
  unpublishEvent: (eventId: string, organizerId?: string) => Promise<boolean>;
  publishEvent: (eventId: string, organizerId?: string) => Promise<PublishEventResult>;
  deleteEvent: (eventId: string) => Promise<boolean>;
  cancelEvent: (eventId: string, reason?: string) => Promise<boolean>;
  restoreEvent: (eventId: string) => Promise<boolean>;
  cancelSeries: (seriesId: string, reason?: string) => Promise<boolean>;
  restoreSeries: (seriesId: string) => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

interface ToastMessage {
  type: 'success' | 'error';
  text: string;
}

/**
 * Hook for event actions: unpublish, publish, delete
 * La duplication se fait côté client (pré-remplissage du formulaire)
 */
export const useEventActions = (
  onSuccess?: (message: ToastMessage) => void
): UseEventActionsReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Unpublish an event
   * Sets status to 'draft' so it's no longer visible to users
   */
  const unpublishEvent = useCallback(async (
    eventId: string,
    organizerId?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventService.updateEvent(eventId, {
        status: 'draft',
        organizerId
      });

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Event unpublished successfully'
      });

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('eventUnpublished', {
        detail: { eventId }
      }));

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to unpublish event';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Publish an event
   * Uses Stripe RPC to verify quota limits server-side
   * Returns detailed result including whether limit was reached
   */
  const publishEvent = useCallback(async (
    eventId: string,
    organizerId?: string
  ): Promise<PublishEventResult> => {
    setIsLoading(true);
    setError(null);

    try {
      // Use Stripe service RPC which handles quota checking server-side
      const result = await stripeService.publishEvent(eventId);

      if (!result.success) {
        // Check if limit was reached
        if (result.error === 'limit_reached') {
          return {
            success: false,
            limitReached: true,
            eventsUsed: result.eventsUsed,
            eventsLimit: result.eventsLimit,
          };
        }

        // Other error
        const errorMessage = result.error || 'Failed to publish event';
        setError(errorMessage);
        onSuccess?.({
          type: 'error',
          text: errorMessage
        });
        return {
          success: false,
          error: errorMessage,
        };
      }

      // Success - notify and dispatch event
      onSuccess?.({
        type: 'success',
        text: result.action === 'first_publish'
          ? 'Event published successfully!'
          : 'Event republished successfully!'
      });

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('eventPublished', {
        detail: { eventId }
      }));

      return {
        success: true,
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to publish event';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return {
        success: false,
        error: errorMessage,
      };

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Delete an event permanently
   */
  const deleteEvent = useCallback(async (
    eventId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventService.deleteEvent(eventId);

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Event deleted successfully'
      });

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('eventDeleted', {
        detail: { eventId }
      }));

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete event';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Cancel an event (keeps it visible with CANCELLED badge)
   */
  const cancelEvent = useCallback(async (
    eventId: string,
    reason?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventService.cancelEvent(eventId, reason);

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Event cancelled successfully'
      });

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel event';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Restore a cancelled event (sets status back to 'active')
   */
  const restoreEvent = useCallback(async (
    eventId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventService.restoreEvent(eventId);

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Event restored successfully'
      });

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore event';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Cancel a series and all non-exception occurrences
   */
  const cancelSeries = useCallback(async (
    seriesId: string,
    reason?: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventSeriesService.cancelEventSeries(seriesId, reason);

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Series cancelled successfully'
      });

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to cancel series';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  /**
   * Restore a cancelled series and all non-exception occurrences
   */
  const restoreSeries = useCallback(async (
    seriesId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await supabaseEventSeriesService.restoreEventSeries(seriesId);

      // Notify success
      onSuccess?.({
        type: 'success',
        text: 'Series restored successfully'
      });

      return true;

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to restore series';
      setError(errorMessage);
      onSuccess?.({
        type: 'error',
        text: errorMessage
      });
      return false;

    } finally {
      setIsLoading(false);
    }
  }, [onSuccess]);

  return {
    unpublishEvent,
    publishEvent,
    deleteEvent,
    cancelEvent,
    restoreEvent,
    cancelSeries,
    restoreSeries,
    isLoading,
    error
  };
};

export default useEventActions;
